import React, { useState, useEffect, useCallback } from 'react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { LoadingSpinner, EmptyState } from '../components/layout/SharedComponents';
import api from '../services/api';
import { toast } from 'sonner';
import {
  Clock, BarChart3, TrendingUp, TrendingDown, Package, AlertTriangle,
  ArrowUpDown, ArrowUp, ArrowDown,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';

const PIE_COLORS = ['#4f46e5', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
const ABC_COLORS = { A: '#ef4444', B: '#f59e0b', C: '#10b981' };

function SortHeader({ label, sortKey, current, onSort, className = '' }) {
  const active = current.key === sortKey;
  const icon = active
    ? (current.dir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
    : <ArrowUpDown className="w-3 h-3 opacity-30" />;
  return (
    <th className={`px-3 py-2 font-semibold select-none cursor-pointer hover:bg-accent/30 transition-colors ${className}`}
      onClick={() => onSort(sortKey)}>
      <span className="inline-flex items-center gap-1">{label} {icon}</span>
    </th>
  );
}

function sortRows(data, key, dir) {
  if (!key) return data;
  return [...data].sort((a, b) => {
    let av = a[key] ?? '', bv = b[key] ?? '';
    if (typeof av === 'number' && typeof bv === 'number') return dir === 'asc' ? av - bv : bv - av;
    return dir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });
}

export default function InventoryAnalytics({ onItemClick }) {
  const [tab, setTab] = useState('aging');

  return (
    <div>
      <div className="flex gap-1 border-b mb-4">
        {[
          { id: 'aging', label: 'Stock Aging', icon: Clock },
          { id: 'abc', label: 'ABC Classification', icon: BarChart3 },
          { id: 'forecast', label: 'Demand Forecast', icon: TrendingUp },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            data-testid={`analytics-tab-${t.id}`}>
            <t.icon className="h-3.5 w-3.5 inline mr-1.5" />{t.label}
          </button>
        ))}
      </div>

      {tab === 'aging' && <StockAgingTab onItemClick={onItemClick} />}
      {tab === 'abc' && <ABCClassificationTab onItemClick={onItemClick} />}
      {tab === 'forecast' && <DemandForecastTab onItemClick={onItemClick} />}
    </div>
  );
}


// ── Stock Aging Tab ──

function StockAgingTab({ onItemClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: 'days_since_movement', dir: 'desc' });
  const toggleSort = (key) => setSort(prev => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
  const fmt = (v) => `$${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  useEffect(() => {
    api.get('/api/inventory/analytics/stock-aging')
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load stock aging data'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;
  if (!data || data.total_items === 0) return <EmptyState title="No stock data" description="No items with positive stock found." />;

  const chartData = data.buckets.map(b => ({
    name: b.bucket + ' days',
    items: b.count,
    value: b.value,
  }));

  return (
    <div className="space-y-4" data-testid="stock-aging-report">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Total Items" value={data.total_items} />
        <SummaryCard label="Total Value" value={fmt(data.total_value)} accent="indigo" />
        <SummaryCard label="Stale (90+ days)" value={data.buckets.filter(b => b.bucket === '91-180' || b.bucket === '180+').reduce((s, b) => s + b.count, 0)} accent="red" />
        <SummaryCard label="Stale Value" value={fmt(data.buckets.filter(b => b.bucket === '91-180' || b.bucket === '180+').reduce((s, b) => s + b.value, 0))} accent="red" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border rounded-xl bg-white p-4">
          <h3 className="font-bold text-sm mb-3">Items by Age Bucket</h3>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="items" fill="#4f46e5" name="Items" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="border rounded-xl bg-white p-4">
          <h3 className="font-bold text-sm mb-3">Value by Age Bucket</h3>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Bar dataKey="value" fill="#f59e0b" name="Value" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Detail table */}
      <div className="border rounded-xl overflow-hidden">
        <table className="w-full text-xs" data-testid="aging-items-table">
          <thead><tr className="bg-muted/50 border-b">
            <SortHeader label="Item Code" sortKey="item_code" current={sort} onSort={toggleSort} className="text-left" />
            <SortHeader label="Description" sortKey="outline" current={sort} onSort={toggleSort} className="text-left" />
            <SortHeader label="Category" sortKey="category" current={sort} onSort={toggleSort} className="text-left" />
            <SortHeader label="Qty" sortKey="total_qty" current={sort} onSort={toggleSort} className="text-right" />
            <SortHeader label="Value" sortKey="total_value" current={sort} onSort={toggleSort} className="text-right" />
            <SortHeader label="Days Idle" sortKey="days_since_movement" current={sort} onSort={toggleSort} className="text-right" />
            <SortHeader label="Age Bucket" sortKey="age_bucket" current={sort} onSort={toggleSort} className="text-left" />
            <th className="text-left px-3 py-2 font-semibold">Last Movement</th>
          </tr></thead>
          <tbody>
            {sortRows(data.items, sort.key, sort.dir).map((item, i) => {
              const bucketColors = {
                '0-30': 'bg-emerald-100 text-emerald-700', '31-60': 'bg-blue-100 text-blue-700',
                '61-90': 'bg-amber-100 text-amber-700', '91-180': 'bg-orange-100 text-orange-700',
                '180+': 'bg-red-100 text-red-700',
              };
              return (
                <tr key={i} className="border-b hover:bg-accent/20 cursor-pointer transition-colors"
                  onClick={() => onItemClick?.(item.item_code)}
                  data-testid={`aging-row-${item.item_code}`}>
                  <td className="px-3 py-2 font-mono font-semibold text-primary">{item.item_code}</td>
                  <td className="px-3 py-2 max-w-[180px] truncate">{item.outline}</td>
                  <td className="px-3 py-2">{item.category}</td>
                  <td className="px-3 py-2 text-right">{item.total_qty}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(item.total_value)}</td>
                  <td className="px-3 py-2 text-right font-semibold">
                    {item.days_since_movement != null ? <span className={item.days_since_movement > 90 ? 'text-red-600' : item.days_since_movement > 60 ? 'text-amber-600' : ''}>{item.days_since_movement}d</span> : <span className="text-red-500">Never</span>}
                  </td>
                  <td className="px-3 py-2"><Badge className={`text-[9px] ${bucketColors[item.age_bucket] || 'bg-slate-100'}`}>{item.age_bucket}</Badge></td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {item.last_movement ? new Date(item.last_movement).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ── ABC Classification Tab ──

function ABCClassificationTab({ onItemClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: 'total_value', dir: 'desc' });
  const [filter, setFilter] = useState('ALL');
  const toggleSort = (key) => setSort(prev => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
  const fmt = (v) => `$${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  useEffect(() => {
    api.get('/api/inventory/analytics/abc-classification')
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load ABC classification'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;
  if (!data || data.total_items === 0) return <EmptyState title="No stock data" description="No items with positive stock found." />;

  const { summary } = data;
  const pieData = [
    { name: `A (${summary.A.count})`, value: summary.A.value, fill: ABC_COLORS.A },
    { name: `B (${summary.B.count})`, value: summary.B.value, fill: ABC_COLORS.B },
    { name: `C (${summary.C.count})`, value: summary.C.value, fill: ABC_COLORS.C },
  ];

  const filtered = filter === 'ALL' ? data.items : data.items.filter(i => i.classification === filter);

  return (
    <div className="space-y-4" data-testid="abc-classification-report">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border rounded-xl p-3 bg-red-50 cursor-pointer hover:shadow-md" onClick={() => setFilter(filter === 'A' ? 'ALL' : 'A')}>
          <div className="flex items-center justify-between">
            <Badge className="bg-red-200 text-red-800 text-xs font-bold">A</Badge>
            <span className="text-[10px] text-red-500">{summary.A.pct}% of value</span>
          </div>
          <p className="text-2xl font-bold text-red-700 mt-1">{summary.A.count}</p>
          <p className="text-[10px] text-red-600">items — {fmt(summary.A.value)}</p>
        </div>
        <div className="border rounded-xl p-3 bg-amber-50 cursor-pointer hover:shadow-md" onClick={() => setFilter(filter === 'B' ? 'ALL' : 'B')}>
          <div className="flex items-center justify-between">
            <Badge className="bg-amber-200 text-amber-800 text-xs font-bold">B</Badge>
            <span className="text-[10px] text-amber-500">{summary.B.pct}% of value</span>
          </div>
          <p className="text-2xl font-bold text-amber-700 mt-1">{summary.B.count}</p>
          <p className="text-[10px] text-amber-600">items — {fmt(summary.B.value)}</p>
        </div>
        <div className="border rounded-xl p-3 bg-emerald-50 cursor-pointer hover:shadow-md" onClick={() => setFilter(filter === 'C' ? 'ALL' : 'C')}>
          <div className="flex items-center justify-between">
            <Badge className="bg-emerald-200 text-emerald-800 text-xs font-bold">C</Badge>
            <span className="text-[10px] text-emerald-500">{summary.C.pct}% of value</span>
          </div>
          <p className="text-2xl font-bold text-emerald-700 mt-1">{summary.C.count}</p>
          <p className="text-[10px] text-emerald-600">items — {fmt(summary.C.value)}</p>
        </div>
        <div className="border rounded-xl p-3">
          <p className="text-lg font-bold text-slate-700">{data.total_items}</p>
          <p className="text-[10px] text-muted-foreground">Total Items</p>
          <p className="text-sm font-bold text-indigo-600 mt-1">{fmt(data.total_value)}</p>
          <p className="text-[10px] text-muted-foreground">Total Value</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border rounded-xl bg-white p-4">
          <h3 className="font-bold text-sm mb-3">Value Distribution</h3>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={40}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Pie>
                <Tooltip formatter={(v) => fmt(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="border rounded-xl bg-white p-4">
          <h3 className="font-bold text-sm mb-3">Cumulative Value Curve (Pareto)</h3>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.items.map((item, i) => ({
                idx: i + 1,
                cumPct: item.cumulative_pct,
                cls: item.classification,
              }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="idx" tick={{ fontSize: 9 }} label={{ value: 'Items (ranked)', position: 'insideBottom', offset: -2, style: { fontSize: 10 } }} />
                <YAxis tick={{ fontSize: 9 }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v) => `${v}%`} />
                <Line type="monotone" dataKey="cumPct" stroke="#4f46e5" strokeWidth={2} dot={false} name="Cumulative %" />
                {/* Reference lines at 80% and 95% */}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Filter buttons */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground font-semibold">Filter:</span>
        {['ALL', 'A', 'B', 'C'].map(f => (
          <Button key={f} size="sm" variant={filter === f ? 'default' : 'outline'} onClick={() => setFilter(f)}
            className={`h-7 text-xs px-3 ${f !== 'ALL' ? `border-2` : ''}`}
            style={f !== 'ALL' ? { borderColor: ABC_COLORS[f], color: filter === f ? 'white' : ABC_COLORS[f], backgroundColor: filter === f ? ABC_COLORS[f] : 'transparent' } : {}}
            data-testid={`abc-filter-${f}`}>
            {f === 'ALL' ? `All (${data.total_items})` : `${f} (${summary[f].count})`}
          </Button>
        ))}
      </div>

      {/* Detail table */}
      <div className="border rounded-xl overflow-hidden">
        <table className="w-full text-xs" data-testid="abc-items-table">
          <thead><tr className="bg-muted/50 border-b">
            <th className="text-left px-3 py-2 font-semibold w-8">Class</th>
            <SortHeader label="Item Code" sortKey="item_code" current={sort} onSort={toggleSort} className="text-left" />
            <SortHeader label="Description" sortKey="outline" current={sort} onSort={toggleSort} className="text-left" />
            <SortHeader label="Category" sortKey="category" current={sort} onSort={toggleSort} className="text-left" />
            <SortHeader label="Qty" sortKey="total_qty" current={sort} onSort={toggleSort} className="text-right" />
            <SortHeader label="Value" sortKey="total_value" current={sort} onSort={toggleSort} className="text-right" />
            <SortHeader label="Value %" sortKey="value_pct" current={sort} onSort={toggleSort} className="text-right" />
            <SortHeader label="Cum. %" sortKey="cumulative_pct" current={sort} onSort={toggleSort} className="text-right" />
            <SortHeader label="Annual Use" sortKey="annual_consumption" current={sort} onSort={toggleSort} className="text-right" />
          </tr></thead>
          <tbody>
            {sortRows(filtered, sort.key, sort.dir).map((item, i) => (
              <tr key={i} className="border-b hover:bg-accent/20 cursor-pointer transition-colors"
                onClick={() => onItemClick?.(item.item_code)}
                data-testid={`abc-row-${item.item_code}`}>
                <td className="px-3 py-2 text-center">
                  <Badge className="text-[10px] font-bold" style={{ backgroundColor: `${ABC_COLORS[item.classification]}20`, color: ABC_COLORS[item.classification] }}>
                    {item.classification}
                  </Badge>
                </td>
                <td className="px-3 py-2 font-mono font-semibold text-primary">{item.item_code}</td>
                <td className="px-3 py-2 max-w-[180px] truncate">{item.outline}</td>
                <td className="px-3 py-2">{item.category}</td>
                <td className="px-3 py-2 text-right">{item.total_qty}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold">{fmt(item.total_value)}</td>
                <td className="px-3 py-2 text-right">{item.value_pct}%</td>
                <td className="px-3 py-2 text-right text-muted-foreground">{item.cumulative_pct}%</td>
                <td className="px-3 py-2 text-right">{item.annual_consumption || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ── Demand Forecast Tab ──

function DemandForecastTab({ onItemClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: 'months_of_supply', dir: 'asc' });
  const [riskFilter, setRiskFilter] = useState('ALL');
  const toggleSort = (key) => setSort(prev => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));

  useEffect(() => {
    api.get('/api/inventory/analytics/demand-forecast?months=3')
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load demand forecast'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;
  if (!data || data.total_items === 0) return <EmptyState title="No consumption data" description="No ISSUE movements found in the last 6 months. Demand forecast requires historical consumption data." />;

  const riskColors = {
    CRITICAL: 'bg-red-100 text-red-700',
    LOW: 'bg-amber-100 text-amber-700',
    OK: 'bg-emerald-100 text-emerald-700',
    EXCESS: 'bg-blue-100 text-blue-700',
  };

  const riskCounts = { CRITICAL: 0, LOW: 0, OK: 0, EXCESS: 0 };
  data.items.forEach(i => { riskCounts[i.risk] = (riskCounts[i.risk] || 0) + 1; });

  const filtered = riskFilter === 'ALL' ? data.items : data.items.filter(i => i.risk === riskFilter);

  // Build consumption trend chart for top items
  const months = data.analyzed_months || [];
  const chartItems = data.items.slice(0, 5);
  const trendData = months.map(m => {
    const point = { month: m };
    chartItems.forEach(item => {
      point[item.item_code] = (item.monthly_history || {})[m] || 0;
    });
    return point;
  });
  const lineColors = ['#4f46e5', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6'];

  return (
    <div className="space-y-4" data-testid="demand-forecast-report">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryCard label="Items Analyzed" value={data.total_items} />
        <SummaryCard label="Months of Data" value={data.months_analyzed} accent="indigo" />
        <div className="border rounded-xl p-3 bg-red-50 cursor-pointer hover:shadow-md" onClick={() => setRiskFilter(riskFilter === 'CRITICAL' ? 'ALL' : 'CRITICAL')}>
          <p className="text-xl font-bold text-red-700">{riskCounts.CRITICAL}</p>
          <p className="text-[10px] text-red-600 font-semibold">Critical (&lt;1 mo supply)</p>
        </div>
        <div className="border rounded-xl p-3 bg-amber-50 cursor-pointer hover:shadow-md" onClick={() => setRiskFilter(riskFilter === 'LOW' ? 'ALL' : 'LOW')}>
          <p className="text-xl font-bold text-amber-700">{riskCounts.LOW}</p>
          <p className="text-[10px] text-amber-600 font-semibold">Low (&lt;2 mo supply)</p>
        </div>
        <div className="border rounded-xl p-3 bg-blue-50 cursor-pointer hover:shadow-md" onClick={() => setRiskFilter(riskFilter === 'EXCESS' ? 'ALL' : 'EXCESS')}>
          <p className="text-xl font-bold text-blue-700">{riskCounts.EXCESS}</p>
          <p className="text-[10px] text-blue-600 font-semibold">Excess (&gt;12 mo supply)</p>
        </div>
      </div>

      {/* Consumption trend chart */}
      {trendData.length > 1 && chartItems.length > 0 && (
        <div className="border rounded-xl bg-white p-4">
          <h3 className="font-bold text-sm mb-3">Monthly Consumption Trend (Top {chartItems.length} Items)</h3>
          <div style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                {chartItems.map((item, idx) => (
                  <Line key={item.item_code} type="monotone" dataKey={item.item_code}
                    stroke={lineColors[idx % lineColors.length]} strokeWidth={2}
                    name={item.item_code} dot={{ r: 3 }} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground font-semibold">Risk Filter:</span>
        {['ALL', 'CRITICAL', 'LOW', 'OK', 'EXCESS'].map(f => (
          <Button key={f} size="sm" variant={riskFilter === f ? 'default' : 'outline'}
            onClick={() => setRiskFilter(f)} className="h-7 text-xs px-3"
            data-testid={`forecast-filter-${f}`}>
            {f === 'ALL' ? `All (${data.total_items})` : `${f} (${riskCounts[f] || 0})`}
          </Button>
        ))}
      </div>

      {/* Detail table */}
      <div className="border rounded-xl overflow-hidden">
        <table className="w-full text-xs" data-testid="forecast-items-table">
          <thead><tr className="bg-muted/50 border-b">
            <SortHeader label="Item Code" sortKey="item_code" current={sort} onSort={toggleSort} className="text-left" />
            <SortHeader label="Description" sortKey="outline" current={sort} onSort={toggleSort} className="text-left" />
            <SortHeader label="Category" sortKey="category" current={sort} onSort={toggleSort} className="text-left" />
            <SortHeader label="Avg Monthly" sortKey="avg_monthly_demand" current={sort} onSort={toggleSort} className="text-right" />
            <SortHeader label={`${data.forecast_months}mo Forecast`} sortKey="forecast_total" current={sort} onSort={toggleSort} className="text-right" />
            <SortHeader label="Current Stock" sortKey="current_stock" current={sort} onSort={toggleSort} className="text-right" />
            <SortHeader label="Mo. of Supply" sortKey="months_of_supply" current={sort} onSort={toggleSort} className="text-right" />
            <SortHeader label="Trend" sortKey="trend_pct" current={sort} onSort={toggleSort} className="text-right" />
            <th className="text-left px-3 py-2 font-semibold">Risk</th>
          </tr></thead>
          <tbody>
            {sortRows(filtered, sort.key, sort.dir).map((item, i) => (
              <tr key={i} className="border-b hover:bg-accent/20 cursor-pointer transition-colors"
                onClick={() => onItemClick?.(item.item_code)}
                data-testid={`forecast-row-${item.item_code}`}>
                <td className="px-3 py-2 font-mono font-semibold text-primary">{item.item_code}</td>
                <td className="px-3 py-2 max-w-[180px] truncate">{item.outline}</td>
                <td className="px-3 py-2">{item.category}</td>
                <td className="px-3 py-2 text-right font-semibold">{item.avg_monthly_demand}</td>
                <td className="px-3 py-2 text-right font-bold text-indigo-600">{item.forecast_total}</td>
                <td className="px-3 py-2 text-right">{item.current_stock}</td>
                <td className="px-3 py-2 text-right">
                  <span className={`font-semibold ${item.risk === 'CRITICAL' ? 'text-red-600' : item.risk === 'LOW' ? 'text-amber-600' : item.risk === 'EXCESS' ? 'text-blue-600' : ''}`}>
                    {item.months_of_supply != null ? `${item.months_of_supply} mo` : '—'}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  {item.trend_pct !== 0 ? (
                    <span className={`inline-flex items-center gap-0.5 ${item.trend_pct > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {item.trend_pct > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {item.trend_pct > 0 ? '+' : ''}{item.trend_pct}%
                    </span>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2">
                  <Badge className={`text-[9px] ${riskColors[item.risk] || 'bg-slate-100'}`}>{item.risk}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ── Shared ──

function SummaryCard({ label, value, accent = 'slate' }) {
  const colors = { slate: 'text-slate-700', indigo: 'text-indigo-600', red: 'text-red-600', amber: 'text-amber-600' };
  return (
    <div className="border rounded-xl p-3 bg-white">
      <p className={`text-xl font-bold ${colors[accent] || colors.slate}`}>{value}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
    </div>
  );
}
